package com.neurobs.ccabserver.transcriptreviewadmin.components.S3Sync

import com.neurobs.ccab.casr.SyncEndEventData
import com.neurobs.ccab.db.EventData
import com.neurobs.ccabserver.transcriptreviewadmin.states.S3SyncSelectionStateGenerator
import com.neurobs.js.ccab.{CASRServer, LogServer}
import com.neurobs.js.util.ServerResponseUtil
import com.neurobs.react.{BooleanAttribute, ReactElementFactory, ReactUtil, ServerDataAttribute, StateAttribute}
import com.neurobs.react.semanticui.{UiButtonFactory, UiCardFactory, UiContainerFactory, UiDividerFactory, UiDropdownFactory, UiGridFactory, UiListFactory, UiLoaderFactory, UiMessageFactory}
import slinky.core.FunctionalComponent
import slinky.core.annotations.react
import slinky.core.facade.Hooks._
import slinky.core.facade.ReactElement
import io.circe.parser.decode
import io.circe.generic.auto._

import scala.scalajs.js

@react
object S3SyncView {
  case class Props(enclosingElement: (ReactElement*) => ReactElement)
  val component = FunctionalComponent[Props] { props =>


    def selectionColumnStyle = Seq[(String, js.Any)](("maxHeight", "30%"))

    props.enclosingElement(
      Seq(
        UiGridFactory.Grid(
          UiGridFactory.GridColumn(
            S3SyncButton(
              UiContainerFactory.Segment(_)("basic")
            ),
            S3SyncStartEventSelection()
          )(("three wide center aligned", selectionColumnStyle)),
          UiGridFactory.GridColumn(
            SyncEventCardGroup()
          )("thirteen wide")
        )
      )
    )
  }

}

@react
object S3SyncButton {
  case class Props(enclosingElement: (ReactElement*) => ReactElement)
  val component = FunctionalComponent[Props] { props =>

    val errorState = StateAttribute(useState[Option[String]](None))
    val callingSyncState = BooleanAttribute(useState(false))

    def handleClick: () => Unit = () => {
      callingSyncState.setValue(true)
      CASRServer.runS3Sync(errorOpt => {
        errorOpt.map(error => errorState.setValue(Some(error.toString)))
        callingSyncState.setValue(false)
      })
    }

    props.enclosingElement(
      Seq[ReactElement](
        if (!callingSyncState.value) UiButtonFactory.Button("Run sync", handleClick = handleClick)("primary")
        else UiButtonFactory.LoadingButton("primary"),
        errorState.value match {
          case None => null
          case Some(error) => UiMessageFactory.BasicErrorMessage("There was an error requesting a server-s3 sync: " + error)
        }
      )


    )

  }
}

@react
object S3SyncStartEventSelection {
  case class Props()
  val component = FunctionalComponent[Props] { _ =>

    val s3SyncSelectionState = S3SyncSelectionStateGenerator.accessState

    def segmentStyle = Seq[(String, js.Any)](("minHeight", "100px"))

    def EventsLoadingElement = () => {
      UiContainerFactory.Segment(
        UiLoaderFactory.ActiveTextLoader("Loading S3 sync events...")("mini")
      )(segmentStyle)
    }

    def ErrorElement = (error: String) => {
      UiMessageFactory.BasicErrorMessage("An error occurred while loading the server s3 sync events: " + error)
    }

    def EventsElement: List[EventData] => ReactElement = (startEvents: List[EventData]) => {
      StartEventSelectionList(startEvents)
    }

    s3SyncSelectionState.startEventsState.ConditionalElements(
      LoadingElement = EventsLoadingElement,
      ErrorElement = ErrorElement,
      DataElement = EventsElement
    )
  }
}

@react
object StartEventSelectionList {
  case class Props(startEvents: List[EventData])
  val component = FunctionalComponent[Props] { props =>

    val s3SyncSelectionState = S3SyncSelectionStateGenerator.accessState

    def handleStartEventClick(startEvent: EventData) = s3SyncSelectionState.displayStartEvent(startEvent)

    def StartEventListItem(startEvent: EventData) = {
      UiListFactory.ListItem(
        handleClick = () => handleStartEventClick(startEvent)
      )(
        startEvent.createdAt.toString
      )
    }

    def selectionListStyle = Seq[(String, js.Any)](("maxHeight", "200px"), ("overflow", "auto"))

    UiListFactory.List(
      props.startEvents.map(StartEventListItem(_))
    )(("selection", selectionListStyle))

  }
}

@react
object SyncEventCardGroup {
  case class Props()
  val component = FunctionalComponent[Props] { props =>

    val s3SyncSelectionState = S3SyncSelectionStateGenerator.accessState

    s3SyncSelectionState.startEventsState.ConditionalElements(
      LoadingElement = () => null,
      ErrorElement = (_: String) => null,
      DataElement = (startEvents: List[EventData]) =>
        UiCardFactory.CardGroup(
          startEvents.map(startEvent => {
            SyncEventCard(startEvent)
          })
        )
    )

  }
}

@react
object SyncEventCard {
  case class Props(startEvent: EventData)
  val component = FunctionalComponent[Props] { props =>

    val s3SyncSelectionState = S3SyncSelectionStateGenerator.accessState
    val syncEventsState = ServerDataAttribute(useState[Either[Option[String], List[EventData]]](Left(None)))

    def isDisplayed = s3SyncSelectionState.startEventIsDisplayed(props.startEvent)

    def hideStartEvent = () => {
      s3SyncSelectionState.hideStartEvent(props.startEvent)
    }

    useEffect(() => {
      if (isDisplayed && syncEventsState.value.isLeft) {
        loadServerSyncEvents()
      }
    }, Vector(s3SyncSelectionState.displayedStartEventsState.value))

    def loadServerSyncEvents() = {
      LogServer.getS3SyncEventsFromStart(props.startEvent.id)(handleServerSyncEvents)
    }

    def handleServerSyncEvents = ServerResponseUtil.handleObjectResultResponse[Seq[EventData]](
      handleObject = data => {
        syncEventsState.setData(data.toList)
      },
      onAnyError = syncEventsState.setError(_)
    )

    def segmentStyle = Seq[(String, js.Any)](("minHeight", "100px"))

    def LoadingContent = () => UiContainerFactory.Segment(
      UiLoaderFactory.ActiveTextLoader("Loading sync events...")
    )(segmentStyle)

    def ErrorContent = (error: String) => UiMessageFactory.BasicErrorMessage(error)

    def EventsContent: List[EventData] => ReactElement = (events: List[EventData]) => {
      SyncEventsDescription(events)
    }

    if (!isDisplayed) null
    else UiCardFactory.Card(
      UiCardFactory.Content(
        UiCardFactory.Header(
          props.startEvent.createdAt.toString,
          UiButtonFactory.IconButton(
            handleClick = hideStartEvent,
            iconName = "close",
          )("red right floated mini")
        ),
        syncEventsState.ConditionalElements(
          LoadingElement = LoadingContent,
          ErrorElement = ErrorContent,
          DataElement = EventsContent
        )
      )
    )
  }
}

@react
object SyncEventsDescription {
  case class Props(syncEvents: List[EventData])
  val component = FunctionalComponent[Props] { props =>

    def endEventOpt = props.syncEvents.find(_.subType.map(_ == "sync_end").getOrElse(false))
    def endEventDataOpt = {
      val endEventContent = endEventOpt.map(_.content.getOrElse(""))
      endEventContent.map(content => decode[SyncEndEventData](content) match {
        case Left(_) => Left(content)
        case Right(data) => Right(data)
      })
    }
    def errorEvents = props.syncEvents.filter(_.subType.map(_ == "sync_error").getOrElse(false))

    UiCardFactory.Description(
      endEventDataOpt match {
        case None => ReactElementFactory.P("No matching end event found in database. Sync may still be running.")
        case Some(endEventEither) => endEventEither match {
          case Left(rawString) =>
            rawString
          case Right(syncEndEventData) =>
            UiContainerFactory.Segment(
              if (syncEndEventData.testTranscriptSyncAttempts == 0) ReactElementFactory.P("0 test transcript syncs")
              else ReactElementFactory.P(s"${syncEndEventData.testTranscriptSyncSuccesses}/${syncEndEventData.testTranscriptSyncAttempts} test transcripts sync attempts were successful"),
              if (syncEndEventData.reviewedTestTranscriptSyncAttempts == 0) ReactElementFactory.P("0 reviewed transcript syncs")
              else ReactElementFactory.P(s"${syncEndEventData.reviewedTestTranscriptSyncSuccesses}/${syncEndEventData.reviewedTestTranscriptSyncAttempts} reviewed transcripts sync attempts were successful"),
              UiDividerFactory.Horizontal,
              errorEvents.filter(_.content.isDefined).map(error => {
                ReactElementFactory.P(error.content.get)
              })
            )("basic")
        }
      }
    )
  }
}